<script setup lang="ts">
import { computed } from "vue";
import Icon from "./Icon.vue";
import WaText from "./WaText.vue";
import { buttonIcon, shownButtons } from "../composables/waButtons";
import { t } from "../i18n";

/**
 * A template as the contact sees it on WhatsApp: header, formatted body, footer and the buttons
 * under the text. Placeholders take the examples when there are any. With more than three buttons
 * WhatsApp shows two and "See all options".
 */
const props = defineProps<{
  header?: string | null | undefined;
  headerExample?: string | null | undefined;
  /** A media header: its kind, and the picture when there is one to show. */
  headerMedia?:
    | {
        format: "image" | "video" | "document";
        url?: string | null | undefined;
        name?: string | null | undefined;
      }
    | null
    | undefined;
  /** A map on top (an appointment's place). */
  headerLocation?: boolean | undefined;
  body: string;
  examples?: string[] | undefined;
  footer?: string | null | undefined;
  buttons?: Array<{ type: string; text: string }> | undefined;
  time?: string | undefined;
}>();

const fill = (text: string, values: Array<string | null | undefined>) =>
  text.replace(/\{\{\s*(\d+)\s*\}\}/g, (all, n: string) => {
    const v = values[Number(n) - 1]?.trim();
    return v || all;
  });
const header = computed(() =>
  props.header ? fill(props.header, [props.headerExample]) : "",
);
const bodyText = computed(() => fill(props.body, props.examples ?? []));
const shown = computed(() => shownButtons(props.buttons));
const time = computed(
  () =>
    props.time ??
    new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
);
</script>
<template>
  <div class="tpl-preview">
    <div class="tpl-bubble">
      <div v-if="headerLocation" class="tpl-media location">
        <Icon name="map" :size="34" /><span class="small">{{
          t("templates.header.location_preview")
        }}</span>
      </div>
      <div v-if="headerMedia" class="tpl-media" :class="headerMedia.format">
        <img
          v-if="headerMedia.format === 'image' && headerMedia.url"
          :src="headerMedia.url"
          alt=""
        />
        <template v-else
          ><Icon
            :name="
              headerMedia.format === 'video'
                ? 'video'
                : headerMedia.format === 'document'
                  ? 'file'
                  : 'image'
            "
            :size="headerMedia.format === 'document' ? 22 : 34"
          /><span v-if="headerMedia.format === 'document'" class="small">{{
            headerMedia.name || t("templates.header.document")
          }}</span></template
        >
      </div>
      <p v-if="header" class="tpl-header">{{ header }}</p>
      <div class="message-text tpl-body" :data-meta="footer ? undefined : time">
        <WaText :text="bodyText" blocks />
      </div>
      <p v-if="footer" class="tpl-footer" :data-meta="time">{{ footer }}</p>
      <span class="message-meta tpl-time">{{ time }}</span>
      <div v-if="shown.length" class="tpl-buttons">
        <span v-for="(b, i) in shown" :key="i" class="tpl-button"
          ><Icon :name="buttonIcon(b.type)" :size="16" />{{ b.text }}</span
        >
      </div>
    </div>
  </div>
</template>
